import type { VfxFeeds } from "./feed.ts";
import type { VfxRegistry } from "./registry.ts";
import { NO_TINT } from "./tone.ts";

/**
 * The vfx player (ADR-134 decision 4): `play(id, target, options)` is the one call a trigger makes. It looks the vfx up,
 * leaves a vfx its definition does not show to others to the page's own character, plays the definition's sound (the
 * owner's hook for the page's own, the others' hook, placed, for another player) and queues the request for the surface of the definition's target.
 */

/**
 * Whom a vfx plays on: the character's handle, whether it is the page's own, and its offset from the listener in tiles,
 * which places the sound of another player's.
 */
export interface VfxTarget {
  readonly handle: number;
  readonly own: boolean;
  readonly dx?: number;
  readonly dy?: number;
}

/**
 * How one play differs from the definition: a tint as 0xRRGGBB and a reach (the multiple of the ring's radius and of the sparks' spread).
 */
export interface VfxOptions {
  readonly tint?: number;
  readonly scale?: number;
}

/**
 * Plays a sound hook for the page's own character or, placed by an offset, for another player.
 */
export type VfxSoundSink = (hook: string, own: boolean, dx: number, dy: number) => void;

/**
 * The player.
 *
 * @example
 * ```ts
 * vfx.play("levelUp", { handle, own: false, dx, dy });
 * vfx.play("drinkBurst", { handle, own: true }, { tint: 0xd04040, scale: 2 });
 * ```
 */
export class VfxPlayer {
  /**
   * @param registry The definitions.
   * @param feeds Where requests go for the surface that draws each target.
   * @param sound Plays sounds; none when the page has no sound.
   */
  public constructor(
    private readonly registry: VfxRegistry,
    private readonly feeds: VfxFeeds,
    private readonly sound: VfxSoundSink | null,
  ) {}

  /**
   * Plays a vfx on a character.
   *
   * @param id The vfx's id.
   * @param target Whom it plays on.
   * @param options The tint and scale of this play.
   * @returns True when it was asked for; false for an id the registry lacks, a vfx hidden from others on another player,
   *   a handle that is not a whole number of 0 or more, or a scale that is not above 0.
   */
  public play(id: string, target: VfxTarget, options: VfxOptions = {}): boolean {
    const key = this.registry.indexOf(id);
    const definition = this.registry.at(key);
    const scale = options.scale ?? 1;
    if (definition === undefined || !Number.isInteger(target.handle) || target.handle < 0 || !(scale > 0) || (!target.own && !definition.others)) {
      return false;
    }
    const hook = target.own ? definition.sound.own : definition.sound.others;
    if (hook !== null) {
      this.sound?.(hook, target.own, target.dx ?? 0, target.dy ?? 0);
    }
    this.feeds[definition.target].push(target.handle, key, target.own, options.tint ?? NO_TINT, scale);
    return true;
  }
}
